import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, UtensilsCrossed, Sparkles, Loader2, Flame, Plus, ShoppingBag, FileText, ChevronRight } from 'lucide-react';
import { formatCurrency } from '../lib/currency';
import GuestCartSheet from '../components/guest/GuestCartSheet';
import { DietaryTagIcons } from '../components/DietaryTags';

const PdfViewer = React.lazy(() => import('../components/PdfViewer'));
const PDF_GROUP = '__pdf__';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const resolveSrc = (url) => (url ? (url.startsWith('http') ? url : `${BACKEND}${url}`) : '');

// Cart persisted per venue session so a reload doesn't wipe the guest's picks.
const cartKey = (code) => `spin.cart.${code}`;
const loadCart = (code) => {
  try { return JSON.parse(window.localStorage.getItem(cartKey(code)) || '[]'); }
  catch { return []; }
};
const saveCart = (code, cart) => {
  try { window.localStorage.setItem(cartKey(code), JSON.stringify(cart)); }
  catch { /* ignore */ }
};

/* ---------- Offers ribbon ---------- */
const OffersMarquee = ({ offers }) => {
  if (!offers?.length) return null;
  const reel = [...offers, ...offers];
  return (
    <div
      className="relative overflow-hidden border-y border-[#D4AF37]/20 bg-gradient-to-r from-[#D4AF37]/8 via-[#D4AF37]/4 to-[#D4AF37]/8"
      data-testid="menu-offers-banner"
    >
      <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-black to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-black to-transparent z-10 pointer-events-none" />
      <div className="flex animate-marquee whitespace-nowrap py-3">
        {reel.map((offer, idx) => (
          <span
            key={`offer-${idx}-${offer.slice(0, 10)}`}
            className="inline-flex items-center mx-8 text-sm text-white font-['Outfit'] font-light tracking-wide"
            data-testid={`menu-offer-${idx % offers.length}`}
          >
            <Sparkles className="w-3.5 h-3.5 text-[#D4AF37] mr-3" />
            <span className="text-[#D4AF37] font-['JetBrains_Mono'] mr-3 text-[10px] uppercase tracking-[0.3em]">Tonight</span>
            {offer}
          </span>
        ))}
      </div>
    </div>
  );
};

/* ---------- Add-to-cart button ---------- */
const AddButton = ({ onAdd, testid }) => (
  <button
    onClick={(e) => { e.stopPropagation(); onAdd(); }}
    data-testid={testid}
    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#D4AF37]/12 hover:bg-[#D4AF37]/25 border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] uppercase tracking-[0.3em] font-['JetBrains_Mono'] font-semibold transition-colors"
  >
    <Plus className="w-3 h-3" strokeWidth={2.5} />
    Order
  </button>
);

const VariationRow = ({ item, v, vi, onAdd }) => {
  const priceNum = Number(v?.price) || 0;
  return (
    <div
      key={`${item.id}-var-${vi}`}
      className="flex items-baseline justify-between gap-3"
      data-testid={`menu-item-${item.id}-variation-${vi}`}
    >
      <span className="inline-flex items-baseline gap-2 min-w-0">
        <span className="text-gray-200 text-sm font-['IBM_Plex_Sans'] truncate">{v.name}</span>
        <DietaryTagIcons value={v.dietary_tags} size={12} className="flex-shrink-0 translate-y-[1px]" />
      </span>
      <span className="flex-1 mx-2 border-b border-dotted border-white/15 translate-y-[-2px]" />
      {priceNum > 0 && (
        <span className="text-white font-['JetBrains_Mono'] font-medium text-sm">{formatCurrency(priceNum)}</span>
      )}
      {onAdd && priceNum > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); onAdd(v); }}
          data-testid={`menu-item-${item.id}-variation-${vi}-add`}
          className="ml-2 w-7 h-7 rounded-full bg-[#D4AF37]/15 hover:bg-[#D4AF37]/30 border border-[#D4AF37]/40 text-[#D4AF37] flex items-center justify-center transition-colors"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={2} />
        </button>
      )}
    </div>
  );
};

/* ---------- Photo card (when item has imagery) ---------- */
const PhotoItemCard = ({ item, onAdd, ordersEnabled }) => {
  const hasVariations = Array.isArray(item.variations) && item.variations.length > 0;
  const img = resolveSrc(item.photo_url);
  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="relative rounded-3xl overflow-hidden border border-white/8 bg-white/[0.02] transform-gpu"
      data-testid={`menu-item-${item.id}`}
    >
      <div className="relative h-56 sm:h-64 overflow-hidden">
        <img src={img} alt={item.name} className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        {item.is_special && (
          <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-black/80 text-[#D4AF37] text-[10px] font-bold uppercase tracking-[0.25em] px-3 py-1.5 rounded-full border border-[#D4AF37]/30">
            <Flame className="w-3 h-3" /> Chef&apos;s
          </div>
        )}
        {/* Always show base price when > 0, even if the dish also has
            variations — clients rely on this so guests see the dish's
            core price alongside optional add-ons. */}
        {item.price > 0 && (
          <div className="absolute top-3 right-3 px-3.5 py-2 rounded-2xl bg-black/85 border border-white/10">
            <p className="text-white font-['JetBrains_Mono'] font-bold text-base leading-none">
              {formatCurrency(item.price)}
            </p>
          </div>
        )}
      </div>

      <div className="relative p-5 pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-white font-['Outfit'] text-2xl leading-tight font-light tracking-tight" data-testid={`menu-item-${item.id}-name`}>
              {item.name}
            </h3>
            <DietaryTagIcons value={item.dietary_tags} size={14} className="mt-1.5" />
          </div>
          {ordersEnabled && !hasVariations && item.price > 0 && (
            <AddButton onAdd={() => onAdd(item, null)} testid={`menu-item-${item.id}-add`} />
          )}
        </div>
        {item.description && (
          <p className="text-gray-400 text-sm mt-2 leading-relaxed font-['IBM_Plex_Sans']">{item.description}</p>
        )}

        {hasVariations && (
          <div className="mt-4 pt-4 border-t border-white/8 space-y-2.5" data-testid={`menu-item-${item.id}-variations`}>
            {item.variations.map((v, vi) => (
              <VariationRow key={vi} item={item} v={v} vi={vi} onAdd={ordersEnabled ? (variant) => onAdd(item, variant) : null} />
            ))}
          </div>
        )}
      </div>
    </motion.article>
  );
};

/* ---------- Text-only card (no imagery) ---------- */
const TextItemCard = ({ item, idx, onAdd, ordersEnabled }) => {
  const hasVariations = Array.isArray(item.variations) && item.variations.length > 0;
  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.45, delay: idx * 0.04 }}
      className="group py-5 border-b border-white/6 last:border-b-0"
      data-testid={`menu-item-${item.id}`}
    >
      <div className="flex items-baseline justify-between gap-4">
        <h3 className="text-white font-['Outfit'] text-xl font-light leading-tight tracking-tight inline-flex items-baseline gap-2 min-w-0" data-testid={`menu-item-${item.id}-name`}>
          <span className="truncate">{item.name}</span>
          <DietaryTagIcons value={item.dietary_tags} size={12} className="translate-y-[2px] flex-shrink-0" />
          {item.is_special && (
            <span className="inline-flex items-center gap-1 ml-1 text-[10px] text-[#D4AF37] uppercase tracking-[0.25em] font-['JetBrains_Mono'] font-bold align-middle">
              <Flame className="w-3 h-3" /> Chef
            </span>
          )}
        </h3>
        <span className="flex-1 mx-2 border-b border-dotted border-white/15 translate-y-[-4px]" />
        {/* Base price — shown whenever the dish has one, even if it also
            has variations. */}
        {item.price > 0 && (
          <p className="text-white font-['JetBrains_Mono'] font-medium text-base whitespace-nowrap">
            {formatCurrency(item.price)}
          </p>
        )}
        {ordersEnabled && !hasVariations && item.price > 0 && (
          <AddButton onAdd={() => onAdd(item, null)} testid={`menu-item-${item.id}-add`} />
        )}
      </div>
      {item.description && (
        <p className="text-gray-400 text-sm mt-1.5 leading-relaxed font-['IBM_Plex_Sans'] max-w-prose">
          {item.description}
        </p>
      )}
      {hasVariations && (
        <div className="mt-3 space-y-1.5" data-testid={`menu-item-${item.id}-variations`}>
          {item.variations.map((v, vi) => (
            <VariationRow key={vi} item={item} v={v} vi={vi} onAdd={ordersEnabled ? (variant) => onAdd(item, variant) : null} />
          ))}
        </div>
      )}
    </motion.article>
  );
};

/* ---------- PDF menu list (shown under the "PDF Menu" group chip) ---------- */
const fmtSize = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const PdfMenuList = ({ pdfs, onOpen }) => (
  <section data-testid="menu-pdf-section">
    <header className="mb-6">
      <p className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-['JetBrains_Mono'] mb-2">
        {String(pdfs.length).padStart(2, '0')} {pdfs.length === 1 ? 'document' : 'documents'}
      </p>
      <h2 className="text-3xl text-white font-['Outfit'] font-extralight tracking-tighter" data-testid="menu-pdf-heading">Full menu</h2>
      <div className="mt-3 h-px bg-gradient-to-r from-[#D4AF37]/40 via-white/10 to-transparent" />
    </header>
    <div className="space-y-3">
      {pdfs.map((p, i) => (
        <motion.button
          key={p.id}
          type="button"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: i * 0.05 }}
          onClick={() => onOpen(p)}
          className="w-full flex items-center gap-4 p-4 rounded-2xl border border-[#D4AF37]/25 bg-[#D4AF37]/[0.06] hover:bg-[#D4AF37]/[0.12] text-left transition-colors"
          data-testid={`menu-pdf-card-${p.id}`}
        >
          <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
            <FileText className="w-6 h-6 text-[#D4AF37]" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white font-['Outfit'] text-lg font-light leading-tight truncate">{p.title}</p>
            <p className="text-[10px] uppercase tracking-[0.3em] text-gray-500 font-['JetBrains_Mono'] mt-1">PDF · {fmtSize(p.size || 0)} · Tap to read</p>
          </div>
          <ChevronRight className="w-5 h-5 text-[#D4AF37]/70 flex-shrink-0" />
        </motion.button>
      ))}
    </div>
  </section>
);

/* ---------- Main page ---------- */
const GuestMenuPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [activeGroup, setActiveGroup] = useState(null);
  const [activeCat, setActiveCat] = useState(null);
  const [activePdf, setActivePdf] = useState(null);
  const [cart, setCart] = useState(() => loadCart(code));
  const [cartOpen, setCartOpen] = useState(false);
  const [ordersEnabled, setOrdersEnabled] = useState(false);
  const sectionRefs = useRef({});

  useEffect(() => {
    axios
      .get(`${API}/menu/public/${code}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || 'Menu unavailable'));
  }, [code]);

  useEffect(() => {
    // Petpooja is mocked-on for demo — this hook lets us cleanly disable the
    // Order button later by flipping a real backend flag.
    axios
      .get(`${API}/petpooja/public-config/${code}`)
      .then((r) => setOrdersEnabled(!!r.data?.is_connected))
      .catch(() => setOrdersEnabled(false));
  }, [code]);

  useEffect(() => { saveCart(code, cart); }, [code, cart]);

  const addToCart = useCallback((item, variation) => {
    setCart((prev) => {
      const price = variation ? Number(variation.price) : Number(item.price);
      const key = `${item.id}::${variation?.name || ''}`;
      const idx = prev.findIndex((c) => `${c.id}::${c.variation_name || ''}` === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: (next[idx].qty || 1) + 1 };
        return next;
      }
      return [
        ...prev,
        { id: item.id, name: item.name, price, qty: 1, variation_name: variation?.name || null },
      ];
    });
  }, []);

  const cartCount = cart.reduce((s, c) => s + (c.qty || 1), 0);
  const cartTotal = cart.reduce((s, c) => s + (Number(c.price) || 0) * (c.qty || 1), 0);

  const pdfMenus = useMemo(() => (Array.isArray(data?.pdf_menus) ? data.pdf_menus : []), [data]);

  const groups = useMemo(() => {
    const gs = data?.items?.length ? [...new Set(data.items.map((i) => i.group).filter(Boolean))].sort() : [];
    return pdfMenus.length ? [...gs, PDF_GROUP] : gs;
  }, [data, pdfMenus]);

  const currentGroup = activeGroup || groups[0] || null;
  const showingPdfs = currentGroup === PDF_GROUP;

  const itemsInGroup = useMemo(() => {
    if (!data?.items) return [];
    if (showingPdfs) return [];
    if (!currentGroup) return data.items;
    return data.items.filter((i) => i.group === currentGroup);
  }, [data, currentGroup, showingPdfs]);

  const categories = useMemo(() => {
    if (!itemsInGroup.length) return [];
    const cats = [...new Set(itemsInGroup.map((i) => i.category || 'Menu'))];
    // Sort sub-categories by admin-set sort_order ASC, then alphabetically.
    // Categories without an explicit order default to 0 — so a positive
    // sort_order value moves a chip earlier (e.g. "Milk Options" before
    // "Cakes" even though "C" comes before "M" alphabetically).
    const orderMap = data?.category_orders || {};
    cats.sort((a, b) => {
      const ao = orderMap[a] || 0;
      const bo = orderMap[b] || 0;
      if (ao !== bo) return ao - bo;
      return a.localeCompare(b, undefined, { sensitivity: 'base' });
    });
    const hasSpecials = itemsInGroup.some((i) => i.is_special);
    return hasSpecials ? ['Specials', ...cats] : cats;
  }, [itemsInGroup, data]);

  const currentCat = activeCat || categories[0] || null;

  useEffect(() => { setActiveCat(null); }, [currentGroup]);

  const itemsByCategory = useMemo(() => {
    if (!itemsInGroup.length) return {};
    const map = {};
    categories.forEach((c) => {
      map[c] = c === 'Specials'
        ? itemsInGroup.filter((i) => i.is_special)
        : itemsInGroup.filter((i) => (i.category || 'Menu') === c);
    });
    return map;
  }, [itemsInGroup, categories]);

  const scrollToCat = (c) => {
    setActiveCat(c);
    // Field bug (Feb 2026): guests reported the page not scrolling back up
    // when a different sub-category was tapped — the tapped section could
    // already be BELOW the current scroll offset, or the section might be
    // hidden entirely (empty category). Fall back to scrolling the window
    // to the top of the menu content so the new section is visible.
    const el = sectionRefs.current[c];
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // On top-level GROUP change (Food ↔ Drinks etc), always snap back to the
  // top of the menu so guests see the new group's first sub-category.
  const changeGroup = (g) => {
    setActiveGroup(g);
    setActiveCat(null); // let currentCat re-derive to the group's first cat
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[#080604] flex items-center justify-center p-6">
        <div className="text-center">
          <UtensilsCrossed className="w-12 h-12 text-gray-700 mx-auto mb-3" />
          <h1 className="text-white font-light text-2xl mb-1 font-['Outfit'] tracking-tight">{error}</h1>
          <button onClick={() => navigate(`/request/${code}`)} className="text-[#D4AF37] text-xs uppercase tracking-[0.3em] font-['JetBrains_Mono'] mt-4 hover:underline">
            ← Back
          </button>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-[#080604] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  const logoSrc = resolveSrc(data.venue_logo_url);

  return (
    <div className="min-h-screen bg-[#080604] text-white flex flex-col" data-testid="guest-menu-page">
      <header className="sticky top-0 z-30 px-5 py-4 bg-[#080604]/85 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate(`/request/${code}`)} className="text-gray-500 hover:text-white transition-colors" data-testid="menu-back">
            <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <p className="text-[10px] text-gray-500 uppercase tracking-[0.4em] font-['JetBrains_Mono']">Menu</p>
          <div className="w-5" />
        </div>
      </header>

      <div className="relative overflow-hidden">
        <motion.div
          aria-hidden
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.5 }}
          transition={{ duration: 2, ease: 'easeOut' }}
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.pexels.com/photos/1267320/pexels-photo-1267320.jpeg?auto=compress&w=1200)' }}
        />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/70 to-[#080604]" />
        <motion.div
          aria-hidden
          className="absolute top-10 -right-20 w-72 h-72 rounded-full bg-[#D4AF37]/15 blur-[100px]"
          animate={{ scale: [1, 1.15, 1] }}
          transition={{ duration: 7, repeat: Infinity }}
        />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="relative max-w-2xl mx-auto px-5 pt-12 pb-10 text-center"
        >
          {logoSrc ? (
            <motion.img
              src={logoSrc}
              alt={data.venue_name}
              className="w-20 h-20 mx-auto rounded-2xl object-cover mb-6 ring-1 ring-[#D4AF37]/40"
              data-testid="menu-venue-logo"
              animate={{ boxShadow: ['0 0 30px rgba(212,175,55,0.25)', '0 0 70px rgba(212,175,55,0.5)', '0 0 30px rgba(212,175,55,0.25)'] }}
              transition={{ duration: 3.5, repeat: Infinity }}
            />
          ) : (
            <div className="w-20 h-20 mx-auto rounded-2xl bg-[#D4AF37]/10 ring-1 ring-[#D4AF37]/30 flex items-center justify-center mb-6">
              <UtensilsCrossed className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.5} />
            </div>
          )}
          <p className="text-[11px] uppercase tracking-[0.5em] text-[#D4AF37] font-['JetBrains_Mono']">
            ✦ Bill of Fare ✦
          </p>
          <h1 className="text-[64px] sm:text-[88px] font-black text-white mt-4 font-['Outfit'] tracking-[-0.05em] leading-[0.85]" data-testid="menu-venue-name">
            {data.venue_name}
          </h1>
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="origin-center mx-auto mt-5 h-1 w-20 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent rounded-full"
          />
          <p className="mt-5 text-[11px] uppercase tracking-[0.4em] text-white/40 font-['JetBrains_Mono']" data-testid="menu-selection-count">
            {String(data.items.length).padStart(2, '0')} {data.items.length === 1 ? 'selection' : 'selections'} · curated tonight
          </p>
        </motion.div>
      </div>

      <OffersMarquee offers={data.offers || []} />

      {groups.length > 1 && (
        <div className="sticky top-[57px] z-30 bg-[#080604]/95 border-b border-white/5" data-testid="menu-groups">
          <div className="max-w-2xl mx-auto px-5 py-3 flex gap-2 overflow-x-auto hide-scrollbar">
            {groups.map((g) => {
              const active = currentGroup === g;
              const isPdf = g === PDF_GROUP;
              return (
                <button
                  key={g}
                  onClick={() => changeGroup(g)}
                  data-testid={isPdf ? 'menu-group-pdf' : `menu-group-${g}`}
                  className={`flex-shrink-0 px-4 h-8 rounded-full text-[11px] uppercase tracking-[0.25em] font-['JetBrains_Mono'] font-semibold transition-colors inline-flex items-center gap-1.5 ${
                    active ? 'bg-[#D4AF37] text-black' : isPdf ? 'bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30 hover:bg-[#D4AF37]/20' : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  {isPdf && <FileText className="w-3 h-3" strokeWidth={2} />}
                  {isPdf ? 'PDF Menu' : g}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {categories.length > 1 && (
        <nav className={`sticky ${groups.length > 1 ? 'top-[113px]' : 'top-[57px]'} z-20 bg-[#080604]/95 border-b border-white/5`}>
          <div className="max-w-2xl mx-auto px-5 overflow-x-auto hide-scrollbar">
            <div className="flex items-stretch gap-7 py-4">
              {categories.map((c) => {
                const active = currentCat === c;
                return (
                  <button
                    key={c}
                    onClick={() => scrollToCat(c)}
                    data-testid={`menu-cat-${c}`}
                    className={`relative flex-shrink-0 text-[11px] uppercase tracking-[0.3em] font-['JetBrains_Mono'] transition-colors pb-2 ${
                      active ? 'text-white' : 'text-gray-600 hover:text-gray-300'
                    }`}
                  >
                    {c === 'Specials' && <Sparkles className="w-2.5 h-2.5 inline mr-1.5 -translate-y-px" />}
                    {c}
                    {active && (
                      <motion.span layoutId="cat-underline" className="absolute -bottom-px left-0 right-0 h-px bg-[#D4AF37]" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </nav>
      )}

      <div className="flex-1 px-5 py-8">
        <div className="max-w-2xl mx-auto space-y-12 pb-32">
          {showingPdfs && <PdfMenuList pdfs={pdfMenus} onOpen={setActivePdf} />}
          {categories.map((c) => {
            const list = itemsByCategory[c] || [];
            if (!list.length) return null;
            return (
              <section
                key={c}
                ref={(el) => { sectionRefs.current[c] = el; }}
                data-testid={`menu-section-${c}`}
                className="scroll-mt-[140px]"
              >
                <header className="mb-6">
                  <p className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-['JetBrains_Mono'] mb-2">
                    {String(list.length).padStart(2, '0')} {list.length === 1 ? 'item' : 'items'}
                  </p>
                  <h2 className="text-3xl text-white font-['Outfit'] font-extralight tracking-tighter">
                    {c === 'Specials' && <Sparkles className="w-5 h-5 text-[#D4AF37] inline mr-2 -translate-y-1" strokeWidth={1.5} />}
                    {c}
                  </h2>
                  <div className="mt-3 h-px bg-gradient-to-r from-[#D4AF37]/40 via-white/10 to-transparent" />
                </header>

                <div className="space-y-4">
                  {list.map((item, i) =>
                    item.photo_url
                      ? <PhotoItemCard key={item.id} item={item} idx={i} onAdd={addToCart} ordersEnabled={ordersEnabled} />
                      : <TextItemCard key={item.id} item={item} idx={i} onAdd={addToCart} ordersEnabled={ordersEnabled} />
                  )}
                </div>
              </section>
            );
          })}
          {data.items.length === 0 && !showingPdfs && (
            <p className="text-gray-500 text-center text-sm py-20 font-['IBM_Plex_Sans']">
              Menu coming soon.
            </p>
          )}
        </div>
      </div>

      {activePdf && (
        <React.Suspense fallback={<div className="fixed inset-0 z-[60] bg-[#080604] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" /></div>}>
          <PdfViewer pdf={activePdf} onClose={() => setActivePdf(null)} />
        </React.Suspense>
      )}

      {/* Floating cart button */}
      {ordersEnabled && cartCount > 0 && !cartOpen && (
        <motion.button
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          onClick={() => setCartOpen(true)}
          className="fixed bottom-5 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 px-6 py-3.5 rounded-full bg-[#D4AF37] hover:bg-[#c19d2f] text-black font-['Outfit'] font-semibold shadow-2xl shadow-[#D4AF37]/40 transition-colors"
          data-testid="cart-open-fab"
        >
          <ShoppingBag className="w-4 h-4" strokeWidth={2} />
          <span className="text-sm">
            {cartCount} item{cartCount === 1 ? '' : 's'}
          </span>
          <span className="text-sm font-['JetBrains_Mono'] font-bold pl-3 border-l border-black/30">
            {formatCurrency(cartTotal)}
          </span>
        </motion.button>
      )}

      <GuestCartSheet
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        cart={cart}
        sessionCode={code}
        onChange={setCart}
      />

      <footer className="border-t border-white/5 px-5 py-6 text-center">
        <p className="text-[10px] uppercase tracking-[0.35em] text-gray-700 font-['JetBrains_Mono']">
          Crafted with care · {data.venue_name}
        </p>
      </footer>
    </div>
  );
};

export default GuestMenuPage;
